/* global __WATCH__:false */
import { Player, PlayerEvent, ErrorEvent } from 'bitmovin-player';
import {
  AdMessageLabel,
  Container,
  ControlBar,
  FullscreenToggleButton,
  PlaybackToggleButton,
  Spacer,
  UIContainer,
  UIManager,
  VolumeSlider,
  VolumeToggleButton,
} from 'bitmovin-player-ui';
import {
  trackAdFinished,
  trackAdSkipped,
  trackAdStarted,
  trackFirstPlay,
  trackPaused,
  trackPlay,
  trackPlaybackFinished,
  trackStallEnded,
  trackStallStarted,
  trackTimeChanged,
  trackVideoReady,
} from 'modules/ensighten_tracking/video/ensightenVideo';

const BITMOVIN_FALLBACK = {
  dash:
    'https://bitdash-a.akamaihd.net/content/MI201109210084_1/mpds/f08e80da-bf1d-4e3d-8899-f0f6155f6efa.mpd',
  hls:
    'https://bitdash-a.akamaihd.net/content/MI201109210084_1/m3u8s/f08e80da-bf1d-4e3d-8899-f0f6155f6efa.m3u8',
  progressive:
    'https://bitdash-a.akamaihd.net/content/MI201109210084_1/MI201109210084_mpeg-4_hd_high_1080p25_10mbits.mp4',
  poster: 'https://bitdash-a.akamaihd.net/content/MI201109210084_1/poster.jpg',
};

export default class BitmovinPlayer {
  constructor({ avsData = false, prerollOnly = false, domElementId = 'bitmovin-player' }) {
    console.log('BitmovinPlayer', domElementId);
    this.domElementId = domElementId;
    this.enableEnsightenTracking = false;
    this.player = null;
    this.imaUIManager = null;
    this.state = {
      firstPlay: true,
      completed: false,
    };
    this.src = null;
    this.ads = [];
    this.prerollOnly = prerollOnly;
    this.avsData = avsData;

    if (prerollOnly) {
      const { src } = avsData;
      this.src = src;
      this.setupPlayer();
      return;
    }

    if (avsData && !prerollOnly) {
      this._getAVSData(this.avsData)
        .done((response) => {
          if (Object.keys(response.resultObj).length === 0) {
            console.error('BITMOVIN :: AVS response: ', response.message);
          } else {
            this.src = response.resultObj.src || '';
            this.setupPlayer();
          }
        })
        .fail((err) => {
          console.error('BITMOVIN :: AVS response: ', err);
        });
    } else {
      console.warn('BITMOVIN :: window.avs_video data missing');
    }
  }

  playVideo({ avsData, prerollOnly = false }) {
    console.log('BITMOVIN.playVideo', avsData.video_id);
    this.avsData = avsData;
    this.state = {
      firstPlay: true,
      completed: false,
    };
    if (avsData && !prerollOnly) {
      this._getAVSData(this.avsData)
        .done((response) => {
          if (Object.keys(response.resultObj).length === 0) {
            console.error('BITMOVIN :: AVS response: ', response.message);
          } else {
            this.src = response.resultObj.src || '';
            this.setupPlayer();
          }
        })
        .fail((err) => {
          console.error('BITMOVIN :: AVS response: ', err);
        });
    } else {
      console.warn('BITMOVIN :: window.avs_video data missing');
    }
  }

  stopVideo() {
    this.player.pause();
  }

  setupPlayer() {
    this.preRollEndSignal = false;
    const { advertising_slug, poster_url, bitmovin_ad_tag } = this.avsData;
    const playerConfig = this._getConfig();
    const playerContainer = document.getElementById(this.domElementId);
    if (document.getElementById('video-placeholder')) {
      document.getElementById('video-placeholder').classList.add('hide');
    }
    this.player = this.player || new Player(playerContainer, playerConfig);
    this.imaUIManager = this.imaUIManager || this._getUIManager(this.player, advertising_slug);
    const playerSource = __WATCH__
      ? BITMOVIN_FALLBACK
      : this.prerollOnly
      ? {
          progressive: [
            {
              url: this.src,
              type: 'video/mp4',
            },
          ],
          poster: poster_url,
        }
      : {
          hls: this.src,
          poster: poster_url,
        };

    this.addListeners();
    this.player.load(playerSource).then(
      () => {
        if (this.enableEnsightenTracking) {
          this.onReady();
        }
      },
      () => {
        if (this.prerollOnly) {
          this.preRollEndSignal && this.preRollEndSignal();
        }
      }
    );
    // __WATCH__ && this.player.setLogLevel('debug');
  }

  pause() {
    this.player.pause();
  }
  play() {
    this.player.play();
  }

  addEnsightenTracking() {
    this.enableEnsightenTracking = true;
    this.addListeners();
  }

  addListeners() {
    if (this.prerollOnly) {
      this.addPreRollEventListeners();
    }
    if (this.player && this.enableEnsightenTracking) {
      this.addPlayerEventListeners();
    }
  }

  onReady() {
    const duration = this.player.getDuration(); // video duration
    trackVideoReady({ duration });
  }
  onAdManifestLoaded({ adConfig = {} }) {
    const { ads = [] } = adConfig;
    this.ads = ads;
    console.log('~~~~~~~~~~~~~ onAdManifestLoaded', adConfig);
  }
  onAdStarted(e) {
    console.log('~~~~~~~~~~~~~ onAdStarted', e);
    this.imaUIManager.currentUi.onControlsShow.dispatch(this.imaUIManager);
    const duration = this.player.getDuration(); // video duration
    trackAdStarted({ duration });
  }
  onAdSkipped(e) {
    console.log('~~~~~~~~~~~~~ onAdSkipped', e);
    trackAdSkipped({});
  }
  onAdFinished(e) {
    console.log('~~~~~~~~~~~~~ onAdFinished', e);
    trackAdFinished({});
  }
  /**
   * onAdError is used for when an ad Tag exists but the response from the server when retrieving the tag is an error
   * helps with this : message: "The VAST response document is empty."
   */
  onAdError(e) {
    console.log('~~~~~~~~~~~~~ onAdError', e);
    trackFirstPlay({});
  }
  onPlaybackFinished(e) {
    console.log('~~~~~~~~~~~~~ onPlaybackFinished', e);
    if (this.prerollOnly) {
      const finishedAd = document.createEvent('Event');
      finishedAd.initEvent('bitmovin_finished', true, true);
      document.dispatchEvent(finishedAd);

      this.state.completed = true;
      this.destroy();
    } else {
      this.state.completed = true;
      trackPlaybackFinished({});
    }
  }
  // onPlay(e) {
  // console.log('~~~~~~~~~~~~~ onPlay', e, 'ads', this.ads);
  // if (this.state.firstPlay) {
  //   this.state.firstPlay = false;
  //   if (!this.state.hasAd) {
  //     trackFirstPlay({});
  //   }
  // } else if (this.state.completed) {
  //   const duration = this.player.getDuration(); // video duration
  //   trackVideoReady({ duration });
  //   trackFirstPlay({});
  //   this.state.completed = false;
  // } else {
  //   trackPlay({});
  // }
  // }
  onPlaying(e) {
    const hasAd = this.ads && this.ads.length > 0;
    console.log('~~~~~~~~~~~~~ onPlaying', e, 'hasAd', hasAd);
    if (this.state.firstPlay) {
      this.state.firstPlay = false;
      if (!hasAd) {
        trackFirstPlay({});
      }
    } else if (this.state.completed) {
      const duration = this.player.getDuration(); // video duration
      trackVideoReady({ duration });
      trackFirstPlay({});
      this.state.completed = false;
    } else {
      trackPlay({});
    }
  }
  onPaused(e) {
    trackPaused({});
  }
  onTimeChanged({ time }) {
    trackTimeChanged({ time });
  }
  onStallStarted(e) {
    trackStallStarted({});
  }
  onStallEnded(e) {
    trackStallEnded({});
  }

  addPlayerEventListeners() {
    // this.player.on(PlayerEvent.Ready, this.onReady.bind(this));
    this.player.on(PlayerEvent.AdManifestLoaded, this.onAdManifestLoaded.bind(this));
    this.player.on(PlayerEvent.AdStarted, this.onAdStarted.bind(this));
    this.player.on(PlayerEvent.AdSkipped, this.onAdSkipped.bind(this));
    this.player.on(PlayerEvent.AdFinished, this.onAdFinished.bind(this));
    this.player.on(PlayerEvent.AdError, this.onAdError.bind(this));
    this.player.on(PlayerEvent.PlaybackFinished, this.onPlaybackFinished.bind(this));
    // this.player.on(PlayerEvent.Play, this.onPlay.bind(this));
    this.player.on(PlayerEvent.Playing, this.onPlaying.bind(this));
    this.player.on(PlayerEvent.Paused, this.onPaused.bind(this));
    this.player.on(PlayerEvent.TimeChanged, this.onTimeChanged.bind(this));
    this.player.on(PlayerEvent.StallStarted, this.onStallStarted.bind(this));
    this.player.on(PlayerEvent.StallEnded, this.onStallEnded.bind(this));
  }

  onAdBreakFinished(e) {
    this.preRollEndSignal && this.preRollEndSignal();

    if (this.prerollOnly) {
      this.destroy();
      const finishedAd = document.createEvent('Event');
      finishedAd.initEvent('bitmovin_finished', true, true);
      document.dispatchEvent(finishedAd);
    }
  }
  addPreRollEventListeners() {
    this.player.on(PlayerEvent.AdBreakFinished, this.onAdBreakFinished.bind(this));
    this.player.on(PlayerEvent.PlaybackFinished, this.onPlaybackFinished.bind(this));
  }

  removePlayerEventListeners() {
    // this.player.off(PlayerEvent.Ready, this.onReady.bind(this));
    this.player.off(PlayerEvent.AdManifestLoaded, this.onAdManifestLoaded.bind(this));
    this.player.off(PlayerEvent.AdStarted, this.onAdStarted);
    this.player.off(PlayerEvent.AdSkipped, this.onAdSkipped);
    this.player.off(PlayerEvent.AdFinished, this.onAdFinished);
    this.player.off(PlayerEvent.AdError, this.onAdError.bind(this));
    this.player.off(PlayerEvent.AdBreakFinished, this.onAdBreakFinished);
    this.player.off(PlayerEvent.PlaybackFinished, this.onPlaybackFinished);
    // this.player.off(PlayerEvent.Play, this.onPlay);
    this.player.off(PlayerEvent.Playing, this.onPlaying.bind(this));
    this.player.off(PlayerEvent.Paused, this.onPaused);
    this.player.off(PlayerEvent.TimeChanged, this.onTimeChanged);
    this.player.off(PlayerEvent.StallStarted, this.onStallStarted);
    this.player.off(PlayerEvent.StallEnded, this.onStallEnded);
  }

  destroy() {
    if (this.player !== null && this.ensightenTrackingEnabled) {
      this.removePlayerEventListeners();
    }
    this.ads = null;
    this.player = null;
    this.imaUIManager = null;
    this.src = null;
  }

  _getUIManager(player, advertising_slug) {
    return new UIManager(player, [
      {
        ui: new UIContainer({
          hideDelay: -1, // always show UI
          components: [
            new Container({
              components: [
                new AdMessageLabel({ text: advertising_slug }),
                //new AdSkipButton(),
              ],
              cssClass: 'ui-ads-status',
            }),
            new ControlBar({
              components: [
                new Container({
                  components: [
                    new PlaybackToggleButton(),
                    new VolumeToggleButton(),
                    new VolumeSlider(),
                    new Spacer(),
                    new FullscreenToggleButton(),
                  ],
                  cssClasses: ['controlbar-bottom'],
                }),
              ],
              cssClasses: ['adControlBar'],
            }),
          ],
          cssClasses: ['ui-skin-ads'],
        }),
        condition: function(context) {
          // Show the ads UI for every ad by ignoring the context.adRequiresUi flag
          return context.isAd;
        },
      },
    ]);
  }

  _getConfig() {
    // const cookiePolicy = window.TURNERENTS.COOKIE_POLICY || {};
    // const oneTrustActive = cookiePolicy.oneTrustActive || false;
    // const advertisingCookiesEnabled = cookiePolicy.advertisingCookiesEnabled || false;
    //
    const { bitmovin_key, bitmovin_ad_tag, autoplay = false, staticUrl } = this.avsData;
    //
    // let playAd = true; // default to always play video prerolls
    // if (oneTrustActive) {
    //   playAd = advertisingCookiesEnabled; // if oneTrustActive only play video prerolls if advertisingCookiesEnabled = true
    // }

    let playAd = false;

    const conf = {
      key: bitmovin_key,
      analytics: false,
      playback: {
        autoplay,
        muted: autoplay,
      },
      location: {
        ui: `${staticUrl}bitmovin/bitmovinplayer-ui.min.js`,
        ui_css: `${staticUrl}bitmovin/bitmovinplayer-ui.min.css`,
      },
    };
    if (bitmovin_ad_tag && playAd) {
      conf.advertising = {
        adBreaks: [
          {
            tag: {
              url: bitmovin_ad_tag,
              type: 'vast',
            },
            id: 'pre-roll-ad',
            position: 'pre',
          },
        ],
      };
    }
    return conf;
  }

  _getAVSData(avsData) {
    const { avs_video_play_url, video_id } = avsData;
    let url = avs_video_play_url.replace('{format}', 'PCTV').replace('{id}', video_id);
    return $.ajax({
      url: url,
      crossDomain: true,
      method: 'GET',
      dataType: 'json',
    });
  }
}
